import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import { ContactProfileState } from '@/entities/enums/ContactProfileState.ts';
import { useFeature } from '@/hooks/useFeature.ts';
import { useAccountStore } from '@/store/modules/account/store.js';

export type CustomerProfileContactState = ContactProfileState | null;

export const useCustomerProfileActivationState = (accountId?: MaybeRefOrGetter<number | null>) => {
    const { isEnabled, isEnabledForAccount } = useFeature();
    const accountStore = useAccountStore();
    const hasExplicitAccount = accountId !== undefined;
    const resolvedAccountId = computed(() => (accountId === undefined ? null : toValue(accountId)));
    const account = computed(() => {
        if (!hasExplicitAccount) {
            return accountStore.contextAccount;
        }

        const id = resolvedAccountId.value;
        return id === null ? null : accountStore.getAccountById(id);
    });

    const contactProfileState = computed<CustomerProfileContactState>(
        () => account.value?.contact_profile_state ?? null
    );
    const isFeatureEnabled = computed(() => {
        if (!hasExplicitAccount) {
            return isEnabled('customer_profile', false);
        }

        const id = resolvedAccountId.value;
        return id === null ? false : isEnabledForAccount(id, 'customer_profile');
    });
    const isOrganizationalParent = computed(() => Boolean(account.value?.children?.length));
    const isReadActivated = computed(
        () =>
            isFeatureEnabled.value &&
            (contactProfileState.value === ContactProfileState.Read ||
                contactProfileState.value === ContactProfileState.ReadWrite)
    );
    const isWriteActivated = computed(
        () =>
            isFeatureEnabled.value &&
            !isOrganizationalParent.value &&
            contactProfileState.value === ContactProfileState.ReadWrite
    );

    return {
        contactProfileState,
        isFeatureEnabled,
        isOrganizationalParent,
        isReadActivated,
        isWriteActivated,
    };
};
